/**
 * Lab test name resolution: printed name -> canonical marker name.
 *
 * Port of lab_name.py — see that file for the rationale. An exact lookup in
 * normalize-name-map.json first, then cleaned-up candidates under guards that
 * keep a cleanup from producing a WRONG canonical.
 *
 * SYNC WARNING: This file must stay in sync with lab_name.py (Python port).
 * Both are pinned by tests/lab-name-cases.json.
 */

const MAX_CANDIDATES = 40;

const URINE_RE = /\burine\b|\burinalysis\b/i;
const DASHES_RE = /[‐-―−]/g;
const FOOTNOTE_RE = /[*†‡]+$|:$/;
// Assay methods labs append to the name; they never change which marker it is.
const METHOD_SUFFIX_RE = /[,\s]+(?:LC\/MS\/MS|LC\/MS-MS|LC-MS\/MS|LC\/MS|MS|IA|ECLIA|ICMA|EIA)\s*$/i;
const BLOOD_SPECIMEN_SUFFIX_RE = /[,\s]+(?:Serum|Plasma|Whole Blood|Blood)\s*$/i;
const PERCENT_SUFFIX_RE = /\s*(?:\(%\)|%|Percent)\s*$/i;
const TOTAL_SUFFIX_RE = /[,\s]+Total\s*$/i;
const TOTAL_PREFIX_RE = /^Total\s+/i;
const FREE_SUFFIX_RE = /^(.+?)[,\s]+Free\s*$/i;
// "Glucose - Urine", "Glucose, Urine", "Glucose (Urine)" -> "Urine Glucose".
const URINE_SUFFIX_RE = /^(.+?)[\s,(-]+Urine\)?\s*$/i;
// "HDLCholesterol" -> "HDL Cholesterol": an uppercase run glued to a capitalized word.
const CAMEL_RE = /([A-Z]+)([A-Z][a-z]{2,})/g;
const PAREN_ABBR_RE = /^(.+?)\s*\(([^()]{2,12})\)\s*$/;
// A trailing abbreviation: short, no spaces, and abbreviation-shaped (2+ capitals,
// or a capital plus a digit) so ordinary words ("Free", "Ratio") never qualify.
const TRAILING_TOKEN_RE = /^(.+?)[\s-]+([A-Za-z0-9-]{2,8})$/;

/** Whole-word, case-insensitive, Unicode-aware ("índice" is one word). */
const words = (list, extra = '') => new RegExp(
  String.raw`(?<![\p{L}\p{N}_])(?:` + list.join('|') + (extra ? `|${extra}` : '') + String.raw`)(?![\p{L}\p{N}_])`,
  'iu');

// Qualifiers that change WHICH marker a name is: present in the name if and only
// if present in the canonical. English plus the Spanish the parser already sees.
const SYMMETRIC_QUALIFIERS = [
  words(['free', 'libre']),
  words(['abs', 'absolute', 'absoluto', 'absolutos']),
  // A spaced slash only: "LC/MS/MS" and "Chol/HDL" are not ratio signals alone.
  words(['ratio', 'relación', 'relacion', 'cociente'], String.raw`(?<=\s)\/(?=\s)`),
  words(['bioavailable', 'biodisponible']),
  words(['index', 'índice', 'indice']),
  words(['uptake']),
  words(['reverse', 'inversa', 'inverso']),
  /(?<![\p{L}\p{N}_])(?:non|no)-|(?<![\p{L}\p{N}_])non\s/iu,
];
const DIRECT_RE = words(['direct', 'directa', 'directo']);
const INDIRECT_RE = words(['indirect', 'indirecta', 'indirecto']);

const collapse = (text) => text.replace(/\s+/g, ' ').trim();

function isAbbreviation(token) {
  const capitals = [...token].filter(ch => ch >= 'A' && ch <= 'Z').length;
  const hasDigit = /\d/.test(token);
  return capitals >= 2 || (capitals >= 1 && hasDigit);
}

/**
 * True when token's letters/digits appear, in order, in words ("MCV" in
 * "Mean Corpuscular Volume"). Rejects a trailing fragment: "T3" does not
 * abbreviate "Reverse Triiodothyronine".
 */
function abbreviates(token, words) {
  const haystack = words.toLowerCase();
  let pos = 0;
  for (const ch of token.toLowerCase()) {
    if (!/[a-z0-9]/.test(ch)) continue;
    pos = haystack.indexOf(ch, pos);
    if (pos === -1) return false;
    pos += 1;
  }
  return true;
}

/** 'urine' when the name says urine, else null (blood is the default). */
export function detectSpecimen(name) {
  return name && URINE_RE.test(name) ? 'urine' : null;
}

const sub = (pattern, repl = '') => (s) => [s.replace(pattern, repl)];

function abbreviation(s) {
  let m = s.match(PAREN_ABBR_RE);
  if (!m) {
    m = s.match(TRAILING_TOKEN_RE);
    if (!(m && isAbbreviation(m[2]))) return [];
  }
  const [, words, token] = m;
  return abbreviates(token, words) ? [words, token] : [words];
}

// Applied in order; each transform runs on every candidate produced so far, so
// earlier (less altered) candidates are always tried first.
const TRANSFORMS = [
  (s) => [s.replace(DASHES_RE, '-').replace(FOOTNOTE_RE, '').trim()],
  sub(METHOD_SUFFIX_RE),
  sub(BLOOD_SPECIMEN_SUFFIX_RE),
  sub(CAMEL_RE, '$1 $2'),
  sub(PERCENT_SUFFIX_RE),
  sub(TOTAL_SUFFIX_RE),
  sub(TOTAL_PREFIX_RE),
  (s) => { const m = s.match(FREE_SUFFIX_RE); return m ? [`Free ${m[1]}`] : []; },
  (s) => { const m = s.match(URINE_SUFFIX_RE); return m ? [`Urine ${m[1]}`] : []; },
  abbreviation,
];

/** Ordered, de-duplicated spellings to try for `name` (the raw name first). */
export function nameCandidates(name) {
  const out = [];
  const seen = new Set();
  const add = (s) => {
    s = collapse(s || '');
    const key = s.toLowerCase();
    if (s && !seen.has(key) && out.length < MAX_CANDIDATES) {
      seen.add(key);
      out.push(s);
    }
  };

  add(name);
  for (const transform of TRANSFORMS) {
    for (const candidate of [...out]) {
      transform(candidate).forEach(add);
    }
  }
  return out;
}

/**
 * Guard a match: qualifiers that change WHICH marker it is must agree (free vs
 * total, absolute vs percent, ratio, bioavailable, index, uptake, reverse, non-);
 * a direct canonical needs "direct" in the name; indirect never matches.
 */
export function qualifiersAgree(name, canonical) {
  if (INDIRECT_RE.test(name)) return false;
  for (const qualifier of SYMMETRIC_QUALIFIERS) {
    if (qualifier.test(name) !== qualifier.test(canonical)) return false;
  }
  if (DIRECT_RE.test(canonical) && !DIRECT_RE.test(name)) return false;
  return true;
}

/**
 * Resolve a printed lab name against {variant.toLowerCase(): canonical}.
 * Returns { canonical, method } — method is 'exact', 'cleaned' or null.
 * A cleaned match must keep the specimen: urine names reach urine canonicals
 * ("Urine Glucose") only, and blood names never reach them.
 */
export function resolveLabName(name, index) {
  const none = { canonical: null, method: null };
  if (!name || typeof name !== 'string' || !index) return none;
  const exact = index[name.trim().toLowerCase()];
  if (exact) return { canonical: exact, method: 'exact' };
  const specimen = detectSpecimen(name);
  for (const candidate of nameCandidates(name).slice(1)) {
    const canonical = index[candidate.toLowerCase()];
    if (canonical && detectSpecimen(canonical) === specimen && qualifiersAgree(name, canonical)) {
      return { canonical, method: 'cleaned' };
    }
  }
  return none;
}
